// VideoShowConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { getVideoShowsPaginados, createVideoShow, updateVideoShow, deleteVideoShow } from '../../../components/http/Actions/VideoShowAction';

// Components
import VideoShowTable from './components/VideoShowTable';
import EditVideoShowModal from './components/EditVideoShowModal';
import CreateVideoShowModal from './components/CreateVideoShowModal';
import ToggleVideoStatusModal from './components/ToggleVideoStatusModal';

import Notification from '../../../components/Notification';
import Pagination from '../../../components/Pagination';
import SearchBar from '../../../components/SearchBar';

const VideoShowConfig = () => {
  // Estado para almacenar la lista de videoShows
  const [videoShows, setVideoShows] = useState([]);
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [videoShowToEdit, setVideoShowToEdit] = useState(null);
  const [videoShowToToggle, setVideoShowToToggle] = useState(null);
  
  // Estado para nuevo videoShow 
  const [newVideoShow, setNewVideoShow] = useState({
    titulo: '',
    descripcion: '',
    fechaEstreno: '',
    imagen: '',
    duracion: 0,
    videoURL: '',
    isPremium: false,
    comediantesIds: [],
    tagsIds: [],
    videoFile: null,
    thumbnailFile: null,
    private: false
  });
  
  // token
  const token = localStorage.getItem("token");
  // Estado para errores y notificaciones
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar videoShows con paginación
  const fetchVideoShows = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    try {
      await getVideoShowsPaginados(
        (data) => {
          if (data && data.data) {
            setVideoShows(data.data.data);
            setPagination({
              currentPage: data.data.currentPage,
              pageSize: data.data.pageSize,
              totalCount: data.data.totalCount,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setVideoShows([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        token
      );
    } catch (error) {
      console.error("Error fetching video shows:", error);
      showNotification("Error al cargar los videos", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Cargar videoShows cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchVideoShows(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchVideoShows, pagination.pageSize]);

  // Función para abrir modal de edición
  const openEditModal = (videoShow) => {
    setVideoShowToEdit({
      ...videoShow,
      fechaEstreno: videoShow.fechaEstreno ? new Date(videoShow.fechaEstreno).toISOString().split('T')[0] : '',
      comediantesIds: videoShow.comediantes 
        ? videoShow.comediantes
            .map(c => c.comedianteId)
            .filter(id => id !== undefined && id !== null) 
        : [],
      tagsIds: videoShow.tags 
        ? videoShow.tags
            .map(t => t.tagId)
            .filter(id => id !== undefined && id !== null)
        : []
    });
    setShowEditModal(true);
  };

  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewVideoShow({
      titulo: '',
      descripcion: '',
      fechaEstreno: '',
      imagen: '',
      duracion: 0,
      videoURL: '',
      isPremium: false,
      comediantesIds: [],
      tagsIds: [],
      videoFile: null,
      thumbnailFile: null,
      private: false
    });
    setShowCreateModal(true);
  };

  // Función para editar videoShow
  const handleEditSubmit = async (e, editedVideoShow) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateVideoShow(editedVideoShow.videoShowId, editedVideoShow, setError, token);
      fetchVideoShows(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Video "${editedVideoShow.titulo}" actualizado con éxito`, "success");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating video show:", error);
      showNotification("Error al actualizar el video", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Función para crear videoShow
  const handleCreateSubmit = async (e, newVideoShowData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // SIEMPRE usar FormData para manejar tanto archivos como JSON
      const formData = new FormData();
      
      // Agregar campos básicos SIEMPRE
      formData.append('titulo', newVideoShowData.titulo);
      formData.append('descripcion', newVideoShowData.descripcion);
      formData.append('fechaEstreno', newVideoShowData.fechaEstreno);
      formData.append('duracion', newVideoShowData.duracion.toString());
      formData.append('isPremium', newVideoShowData.isPremium.toString());
      formData.append('private', newVideoShowData.private.toString());
      
      // Agregar URLs solo si están presentes y no están vacías
      if (newVideoShowData.videoURL && newVideoShowData.videoURL.trim() !== '') {
        formData.append('videoURL', newVideoShowData.videoURL);
      }
      if (newVideoShowData.imagen && newVideoShowData.imagen.trim() !== '') {
        formData.append('imagen', newVideoShowData.imagen);
      }
      
      // Agregar archivos solo si están presentes
      if (newVideoShowData.videoFile) {
        formData.append('videoFile', newVideoShowData.videoFile);
      }
      if (newVideoShowData.thumbnailFile) {
        formData.append('thumbnailFile', newVideoShowData.thumbnailFile);
      }
      
      // Agregar arrays de IDs
      newVideoShowData.comediantesIds.forEach(id => {
        formData.append('comediantesIds', id);
      });
      
      newVideoShowData.tagsIds.forEach(id => {
        formData.append('tagsIds', id);
      });

      console.log('Enviando FormData:', {
        titulo: newVideoShowData.titulo,
        hasVideoFile: !!newVideoShowData.videoFile,
        hasVideoURL: !!newVideoShowData.videoURL,
        hasThumbnailFile: !!newVideoShowData.thumbnailFile,
        hasImageURL: !!newVideoShowData.imagen,
        comediantesCount: newVideoShowData.comediantesIds.length,
        tagsCount: newVideoShowData.tagsIds.length
      });

      // Usar la función createVideoShow existente
      const result = await createVideoShow(formData, setError, token);
      console.log('Video creado exitosamente:', result);
      
      // Actualizar la lista
      await fetchVideoShows(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Video "${newVideoShowData.titulo}" creado con éxito`, "success");
      setShowCreateModal(false);
      
      // Resetear el formulario
      setNewVideoShow({
        titulo: '',
        descripcion: '',
        fechaEstreno: '',
        imagen: '',
        duracion: 0,
        videoURL: '',
        isPremium: false,
        comediantesIds: [],
        tagsIds: [],
        videoFile: null,
        thumbnailFile: null
      });
      
    } catch (error) {
      console.error("Error creating video show:", error);
      showNotification(`Error al crear video: ${error}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Función para eliminar videoShow
  const handleDeleteVideoShow = async (videoShowId, titulo) => {
    if (window.confirm(`¿Estás seguro de eliminar el video "${titulo}"?`)) {
      setIsLoading(true);
      
      try {
        await deleteVideoShow(videoShowId, setError, token);
        fetchVideoShows(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
        showNotification(`Video "${titulo}" eliminado con éxito`, "success");
      } catch (error) {
        console.error("Error deleting video show:", error);
        showNotification("Error al eliminar el video", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Función para abrir el modal de cambio de estado
  const handleToggleStatusClick = (video) => {
    setVideoShowToToggle(video);
    setShowToggleModal(true);
  };

  // Función para cambiar estado del video (publicar/despublicar)
  const handleToggleStatus = async (video) => {
    setIsLoading(true);
    
    try {
      // Solo enviamos los campos que la API espera recibir
      const videoActualizado = {
        videoShowId: video.videoShowId,
        titulo: video.titulo,
        descripcion: video.descripcion,
        fechaEstreno: video.fechaEstreno,
        imagen: video.imagen,
        duracion: video.duracion,
        videoURL: video.videoUrl, // Aseguramos que el nombre del campo sea correcto
        habilitado: !video.habilitado, // Cambiamos el estado
        isPremium: video.isPremium,
        comediantesIds: video.comediantes 
          ? video.comediantes.map(c => c.comedianteId)
          : [],
        tagsIds: video.tags 
          ? video.tags.map(t => t.tagId)
          : []
      };
      
      console.log("Enviando datos para actualizar:", videoActualizado);
      
      // Usamos la función de actualización
      await updateVideoShow(video.videoShowId, videoActualizado, setError, token);
      fetchVideoShows(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Video "${video.titulo}" ${!video.habilitado ? "publicado" : "despublicado"} con éxito`, "success");
      setShowToggleModal(false);
    } catch (error) {
      console.error(`Error al cambiar estado del video:`, error);
      showNotification("Error al cambiar el estado del video", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Videos</h1>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Nuevo Video
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Buscar por título, comediante o tag..."
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* VideoShows table */}
        {!isLoading && !error && (
          <VideoShowTable 
            videoShows={videoShows} 
            onEdit={openEditModal} 
            onDelete={handleDeleteVideoShow} 
            onToggleStatus={handleToggleStatusClick}
          />
        )}

        {/* Paginación */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Edit modal */}
      {showEditModal && videoShowToEdit && (
        <EditVideoShowModal 
          videoShowToEdit={videoShowToEdit}
          setVideoShowToEdit={setVideoShowToEdit}
          handleEditSubmit={handleEditSubmit}
          isLoading={isLoading}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateVideoShowModal 
          newVideoShow={newVideoShow}
          setNewVideoShow={setNewVideoShow}
          handleCreateSubmit={handleCreateSubmit}
          isLoading={isLoading}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Toggle Status modal */}
      {showToggleModal && videoShowToToggle && (
        <ToggleVideoStatusModal 
          video={videoShowToToggle}
          onConfirm={handleToggleStatus}
          onClose={() => setShowToggleModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default VideoShowConfig;